import React, { useEffect, useRef, useState } from 'react';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import config from '../../config';
import css from './SubscriptionPage.css';
import Button from './Button';

const ChangeCardForm = ({ setupIntentSecret, user, onSave, onCancel, isSyncing, intl }) => {
  const cardElementRef = useRef(null);
  const stripeInstance = useRef(null);
  const cardInstance = useRef(null);

  const [error, setError] = useState(null);
  const [isStripeProcessing, setIsStripeProcessing] = useState(false);

  useEffect(() => {
    // Inicjalizacja Stripe i Elements
    if (window.Stripe && !cardInstance.current && cardElementRef.current) {
      if (!stripeInstance.current) {
        stripeInstance.current = window.Stripe(config.stripe.publishableKey);
      }

      const elements = stripeInstance.current.elements();
      
      cardInstance.current = elements.create('card', {
        hidePostalCode: true,
        style: {
          base: {
            fontSize: '16px',
            color: '#1a1f36',
            fontFamily: 'inherit',
            '::placeholder': {
              color: '#a3acb9',
            },
          },
          invalid: {
            color: '#f44336',
            iconColor: '#f44336',
          },
        },
      });
      
      cardInstance.current.mount(cardElementRef.current);
    }

    // Bezpieczny cleanup z przechwytywaniem błędów destrukcji
    return () => {
      if (cardInstance.current) {
        try {
          cardInstance.current.destroy();
        } catch (e) {
          // Ignorujemy wyjątek, jeśli element został już zniszczony w tle
        }
        cardInstance.current = null;
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!setupIntentSecret) {
      setError(intl.formatMessage({ id: 'ChangeCardForm.sessionError' }));
      return;
    }

    if (!cardInstance.current) {
      setError(intl.formatMessage({ id: 'ChangeCardForm.cardElementError' }));
      return;
    }

    if (isStripeProcessing || isSyncing) return;

    setIsStripeProcessing(true);
    setError(null);

    const firstName = user?.attributes?.profile?.firstName || user?.firstName || '';
    const lastName = user?.attributes?.profile?.lastName || user?.lastName || '';
    const email = user?.attributes?.email || user?.email || '';

    try {
      const result = await stripeInstance.current.confirmCardSetup(setupIntentSecret, {
        payment_method: {
          card: cardInstance.current,
          billing_details: {
            name: `${firstName} ${lastName}`.trim(),
            email: email,
          },
        },
      });

      if (result.error) {
        setError(result.error.message);
        setIsStripeProcessing(false);
      } else if (result.setupIntent?.status === 'succeeded') {
        onSave();
      }
    } catch (err) {
      setError(err.message);
      setIsStripeProcessing(false);
    }
  };

  const isLoading = isStripeProcessing || isSyncing;

  return (
    <form onSubmit={handleSubmit}>
      <p className={css.modalDescription}>
        <FormattedMessage id="ChangeCardForm.modalDescription" />
      </p>

      <div className={css.stripeInputContainer}>
        <div ref={cardElementRef} className={css.cardElement} />
      </div>

      {error && <div className={css.errorAlert} style={{ marginTop: '15px' }}>{error}</div>}

      <div className={css.modalActions}>
        <Button 
          type="button" 
          className={css.cancelBtn} 
          onClick={onCancel}
          disabled={isLoading}
          variant="secondary"
        >
          <FormattedMessage id="ChangeCardForm.cancelBtn" />
        </Button>
        <Button 
          type="submit" 
          className={css.saveBtn} 
          disabled={isLoading}
        >
          {isLoading ? (
            <FormattedMessage id="ChangeCardForm.processing" />
          ) : (
            <FormattedMessage id="ChangeCardForm.submitBtn" />
          )}
        </Button>
      </div>
    </form>
  );
};

export default injectIntl(ChangeCardForm);